import {
    Box,
    ButtonBase,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Stack,
    Typography,
} from '@mui/material';
import { ReactElement, cloneElement } from 'react';

import useThemeBreakpoint from 'modules/common/useThemeBreakpoint';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import DialogCloseButton from 'modules/common/components/MUI/DialogCloseButton/DialogCloseButton';

export interface PopoverOption {
    label: string;
    icon: ReactElement;
    onClick: () => void;
}

interface NewMessageOptionsPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    options: PopoverOption[];
}

interface PopoverComponentProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    options: PopoverOption[];
    onOptionClick: (option: PopoverOption, index: number) => void;
}

function DesktopPopover({ open, anchorEl, onClose, options, onOptionClick }: PopoverComponentProps) {
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 2,
            }}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            slotProps={{
                paper: {
                    elevation: 3,
                    sx: { minWidth: 200 },
                },
            }}>
            <List sx={{ py: 1 }}>
                {options.map((option, index) => {
                    const desktopIcon = cloneElement(option.icon, {
                        sx: { fontSize: '16px' },
                    });

                    return (
                        <ListItem key={option.label} disablePadding>
                            <ListItemButton onClick={() => onOptionClick(option, index)}>
                                <ListItemIcon sx={{ minWidth: 24 }}>{desktopIcon}</ListItemIcon>
                                <ListItemText
                                    primary={option.label}
                                    slotProps={{
                                        primary: {
                                            variant: 'body1',
                                            color: 'text.primary',
                                        },
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Popover>
    );
}

interface MobileDrawerProps {
    open: boolean;
    onClose: () => void;
    options: PopoverOption[];
    onOptionClick: (option: PopoverOption, index: number) => void;
}

function MobileDrawer({ open, onClose, options, onOptionClick }: MobileDrawerProps) {
    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 2,
            }}>
            <Box sx={{ pt: 5, px: 3, pb: 3 }}>
                <DialogCloseButton onClick={onClose} data-testid="dialog-close-button" />
                <Stack spacing={4} sx={{ pt: 2 }}>
                    {options.map((option, index) => {
                        const mobileIcon = cloneElement(option.icon, {
                            sx: { fontSize: '24px' },
                        });

                        return (
                            <ButtonBase
                                key={option.label}
                                onClick={() => onOptionClick(option, index)}
                                sx={{
                                    width: '100%',
                                    textAlign: 'left',
                                }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                    {mobileIcon}
                                    <Typography variant="h4">{option.label}</Typography>
                                </Stack>
                            </ButtonBase>
                        );
                    })}
                </Stack>
            </Box>
        </Drawer>
    );
}

export default function NewMessageOptionsPopover({ open, anchorEl, onClose, options }: NewMessageOptionsPopoverProps) {
    const breakpoint = useThemeBreakpoint();
    const isDesktop = breakpoint === 'large';

    const handleOptionClick = (option: PopoverOption, index: number) => {
        collectiveTracking.track({
            name: 'Message Center Option Selected',
            properties: {
                option_label: option.label,
                option_index: index,
                category: 'message_center',
            },
        });

        option.onClick();
        onClose();
    };

    return isDesktop ? (
        <DesktopPopover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            options={options}
            onOptionClick={handleOptionClick}
        />
    ) : (
        <MobileDrawer open={open} onClose={onClose} options={options} onOptionClick={handleOptionClick} />
    );
}
